import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import environment from '../../environments/environment';
import { IOrganization } from '../shared/organization.interface';
import { IUser, IUserResponse } from '../shared/user.interface';
import { IStudy, IStudyResponse } from '../shared/study.interface';
import { ISiteResponse } from '../shared/site.interface';
import { RolePermission } from '@shared/interfaces/permissions.interface';
import { filter, map, Observable } from 'rxjs';
import {
  constructPermissionListUtil,
  DeleteRole,
  PermissionsList,
  Role,
  UserRole,
} from '@data-access/roles';

import { convertKeysToCamelCase } from '@shared/utils';
import {
  ConfigurationsDeleteMessage,
  DOCUMENT_TYPE_URL,
  DocumentAttribute,
  DocumentTypePayload,
  DocumentTypes,
  DocumentTypesData,
  IrisMapping,
  SDocumentPayload,
  SubmissionDocument,
  ConfigSubmissions,
  ADMIN_WORKSPACE_URL,
  ConfigSubmissionsData,
  DocumentTypesConfig,
} from '@data-access/configurations';
import { FullNamePipe } from '@shared/pipes/fullname.pipe';
import { RoleResponse, Permission } from '@team-management/models/roles';

@Injectable({
  providedIn: 'root',
})
export class AdminService {
  private readonly _fullName = inject(FullNamePipe);
  private readonly _http = inject(HttpClient);

  getAllUsers(queryParams: HttpParams, isInternal = false) {
    queryParams = queryParams.append('is_internal', isInternal);
    return this._http.get<IUserResponse>(
      `${environment.irisApi}/auth/admin/user/`,
      {
        params: queryParams,
      },
    );
  }

  getAllUsersAdmin(search: any, page = 1, pageSize = 1000) {
    let query = `${environment.irisApi}/auth/admin/user/?page=${page}&page_size=${pageSize}`;
    query += search ? `&query=${search}` : '';
    return this._http.get<IUser[]>(query);
  }

  getAllRoles(pageSize = 1000, page = 1, search?: string) {
    let query = `${environment.irisApi}/auth/admin/roles/?page=${page}&page_size=${pageSize}`;
    query += search ? `&query=${search}` : '';
    return this._http.get<RoleResponse>(query);
  }
  getAllInternalRoles(pageSize = 100, page = 1, search?: string) {
    let query = `${environment.irisApi}/auth/admin/roles/?page=${page}&page_size=${pageSize}`;
    query += search ? `&query=${search}` : '';
    query += `&is_internal=${'true'}`;
    return this._http.get<RoleResponse>(query);
  }

  getAllPermissions() {
    return this._http.get<Permission[]>(
      `${environment.irisApi}/auth/permission/`,
    );
  }

  createUserRole(userRole: UserRole): Observable<Role> {
    const url = `${environment.irisApi}/auth/admin/role/`;
    return this._http.post<Role>(url, userRole);
  }

  fetchCurrentUserRole(roleId: string): Observable<Role> {
    const url = `${environment.irisApi}/auth/admin/role/${roleId}/`;
    return this._http.get<Role>(url);
  }

  updateUserRole(roleId: string, userRole: UserRole): Observable<Role> {
    const url = `${environment.irisApi}/auth/admin/role/${roleId}/`;
    return this._http.put<Role>(url, userRole);
  }

  checkRoleDeleteEligibility(role: Role): Observable<DeleteRole> {
    const url = `${environment.irisApi}/auth/admin/role/${role.id}/candelete/`;
    return this._http
      .get<DeleteRole>(url)
      .pipe(map((response) => <DeleteRole>convertKeysToCamelCase(response)));
  }

  deleteUserRole(roleId: string, reasonForDelete: string): Observable<Role> {
    const url = `${environment.irisApi}/auth/admin/role/${roleId}/?reason_for_deletion=${reasonForDelete}`;
    return this._http.delete<Role>(url);
  }

  updateUser(user_id: string, userProperties: any) {
    return this._http.put<IUser>(
      `${environment.irisApi}/auth/user/${user_id}/`,
      userProperties,
    );
  }

  updateUserAdmin(user_id: string, userProperties: any) {
    return this._http.put<IUser>(
      `${environment.irisApi}/auth/admin/user/${user_id}/`,
      userProperties,
    );
  }

  getAllSites() {
    return this._http.get<ISiteResponse>(`${environment.irisApi}/site/`);
  }

  getAllOrganizations() {
    return this._http.get<IOrganization[]>(
      `${environment.irisApi}/organization/`,
    );
  }

  getOrganizationsPageSize(page_size: number) {
    return this._http.get<IOrganization[]>(
      `${environment.irisApi}/organization/?page_size=${page_size}`,
    );
  }

  addRole(role: Role) {
    return this._http.post<Role>(`${environment.irisApi}/role/`, role);
  }

  addOrganization(organization: IOrganization) {
    return this._http.post<IOrganization>(
      `${environment.irisApi}/organization/`,
      organization,
    );
  }

  getAllStudies() {
    return this._http.get<IStudyResponse>(`${environment.irisApi}/study/`);
  }

  getSitesForStudy(studyId: string) {
    return this._http.get<ISiteResponse>(
      `${environment.irisApi}/study/${studyId}/site/`,
    );
  }

  createStudy(study: any): Observable<IStudy> {
    return this._http.post<IStudy>(`${environment.irisApi}/study/`, study);
  }

  addChoice(choice: any) {
    return this._http.post<any>(
      `${environment.irisApi}/question/${choice.question_id}/choice/`,
      choice,
    );
  }

  saveChoice(choice: any) {
    return this._http.put<any>(
      `${environment.irisApi}/question/${choice.question_id}/choice/${choice.id}/`,
      choice,
    );
  }

  deleteChoice(choice: any) {
    return this._http.delete<any>(
      `${environment.irisApi}/question/${choice.question_id}/choice/${choice.id}/`,
    );
  }

  addQuestion(question: any) {
    return this._http.post<any>(
      `${environment.irisApi}/module/${question.module_id}/question/`,
      question,
    );
  }

  saveQuestion(question: any) {
    return this._http.put<any>(
      `${environment.irisApi}/module/${question.module_id}/question/${question.question_id}/`,
      question,
    );
  }

  deleteQuestion(question: any) {
    return this._http.delete<any>(
      `${environment.irisApi}/module/${question.module_id}/question/${question.id}/`,
    );
  }

  addModule(module: any) {
    return this._http.post<any>(`${environment.irisApi}/module/`, module);
  }

  saveModule(module: any) {
    return this._http.put<any>(
      `${environment.irisApi}/module/${module.id}/`,
      module,
    );
  }

  deleteModule(module: any) {
    return this._http.delete<any>(
      `${environment.irisApi}/module/${module.id}/`,
      module,
    );
  }

  addQuestionnaire(questionnaire: any) {
    return this._http.post<any>(
      `${environment.irisApi}/questionnaire/admin/`,
      questionnaire,
    );
  }

  getAllQuestionTypes() {
    return this._http.get<any>(`${environment.irisApi}/question_type/`);
  }

  getQuestionnaire(workspace_type_id: string, questionnaire_id?: string) {
    let httpParams = new HttpParams();
    if (workspace_type_id) {
      httpParams = httpParams.set('workspace_type_id', `${workspace_type_id}`);
    }

    if (questionnaire_id) {
      httpParams = httpParams.set('questionnaire_id', `${questionnaire_id}`);
    }

    return this._http.get<any>(`${environment.irisApi}/questionnaire/`, {
      params: httpParams,
    });
  }

  getAllChoiceTypes() {
    return this._http.get<any>(`${environment.irisApi}/choice_types/`);
  }

  getAllPermissionList() {
    return this._http
      .get<PermissionsList>(`${environment.irisApi}/auth/admin/permissions/`)
      .pipe(
        map((permissions: PermissionsList) =>
          constructPermissionListUtil(permissions),
        ),
      );
  }

  getRoleDetails(role_id: string) {
    return this._http.get<any>(
      `${environment.irisApi}/auth/admin/role/${role_id}/`,
    );
  }

  createUserRolePermission(roleObject: any) {
    return this._http.post<any>(
      `${environment.irisApi}/auth/admin/role/`,
      roleObject,
    );
  }

  updateUserRolePermission(roleObject: any, role_id: string) {
    return this._http.put<any>(
      `${environment.irisApi}/auth/admin/role/${role_id}/`,
      roleObject,
    );
  }

  getUserRolePermissionList() {
    return this._http.get<RolePermission[]>(
      `${environment.irisApi}/auth/admin/user/permission/`,
    );
  }

  getAttributeList() {
    return this._http.get<string[]>(
      `${environment.irisApi}/auth/user-attributes/`,
    );
  }

  getAllSmeQCUsers(search?: string) {
    const query = `${environment.irisApi}/workspace/user-attributes/users/${search ? `?query=${search}` : ''}`;

    return this._http.get<any[]>(query).pipe(
      filter((users: any) => users?.data?.length > 0),
      map((users: any) => {
        const userDDListSME: any[] = [];
        const userDDListQC: any[] = [];

        users.data.forEach((item: any) => {
          const attributes = item?.attribute?.toLowerCase();
          if (!attributes) return;

          const user = {
            id: item.id,
            name: this._fullName.transform(item, 'lastFirst'),
          };

          if (attributes.includes('sme') || attributes.includes('manager')) {
            userDDListSME.push(user);
          }
          if (attributes.includes('qc') || attributes.includes('manager')) {
            userDDListQC.push(user);
          }
        });

        return {
          data: users.data,
          userDDListSME,
          userDDListQC,
        };
      }),
    );
  }

  getAllSmeQCUsersForSubmission(search?: string) {
    const query = `${environment.irisApi}/workspace/user-attributes/users/${search ? `?query=${search}` : ''}`;

    return this._http.get<any[]>(query).pipe(
      filter((users: any) => users?.data?.length > 0),
      map((users: any) => {
        const userDDListSME: any[] = [];
        const userDDListQC: any[] = [];

        users.data.forEach((item: any) => {
          const attributes = item?.attribute?.toLowerCase();
          if (!attributes) return;

          const user = {
            id: item.id,
            name: this._fullName.transform(item, 'lastFirst'),
          };

          if (attributes.includes('sme') || attributes.includes('manager')) {
            userDDListSME.push(user);
          }
          if (attributes.includes('qc') || attributes.includes('manager')) {
            userDDListQC.push(user);
          }
        });

        return {
          data: users.data,
          userDDListSME,
          userDDListQC,
        };
      }),
    );
  }

  getAllIntegrations(search?: string) {
    const query = `${environment.irisApi}/external/client/`;
    if (search) {
      let httpParams = new HttpParams();
      httpParams = httpParams.set('query', `${search}`);
      return this._http.get<any[]>(query, {
        params: httpParams,
      });
    }
    return this._http.get<any[]>(query);
  }

  submitIntegration(integration: any) {
    return this._http.post<any>(
      `${environment.irisApi}/external/client/`,
      integration,
    );
  }

  getAllDocumentTypeList(queryParams: HttpParams) {
    return this._http.get<DocumentTypes>(`${DOCUMENT_TYPE_URL}`, {
      params: queryParams,
    });
  }

  getAllDocumentTypesList(queryParams: HttpParams) {
    return this._http.get<DocumentTypesConfig>(`${DOCUMENT_TYPE_URL}`, {
      params: queryParams,
    });
  }

  getAllAttributes(params: HttpParams) {
    const url = `${environment.irisApi}/document/attribute-type/`;
    return this._http.get<DocumentAttribute>(url, {
      params: params,
    });
  }

  createDocumentType(data: DocumentTypePayload) {
    const url = `${DOCUMENT_TYPE_URL}`;
    return this._http.post<DocumentTypesData>(url, data);
  }

  updateDocumentType(data: DocumentTypePayload) {
    const url = `${DOCUMENT_TYPE_URL}${data.id}/`;
    return this._http.put<DocumentTypesData>(url, data);
  }

  checkCanDeleteDocument(documentId: string) {
    const url = `${DOCUMENT_TYPE_URL}${documentId}/can_delete`;
    return this._http.get<ConfigurationsDeleteMessage>(url);
  }

  deleteDocumentType(documentId: string, message: string) {
    const url = `${DOCUMENT_TYPE_URL}${documentId}/delete/`;
    return this._http.put(url, {
      reason_for_deletion: message,
    });
  }

  getAllSubmissionsList(queryParams: HttpParams) {
    return this._http.get<ConfigSubmissions>(`${ADMIN_WORKSPACE_URL}`, {
      params: queryParams,
    });
  }

  getSubmissionDocumentTypeList(
    data: ConfigSubmissionsData,
  ): Observable<SubmissionDocument> {
    const url = `${ADMIN_WORKSPACE_URL}${data.id}/?entity_type=${data.entity_type}`;
    return this._http.get<SubmissionDocument>(url);
  }

  getSubmissionDocumentTypes(entityType: string): Observable<DocumentTypes> {
    const url = `${environment.irisApi}/document/type/?entity_type=${entityType}`;
    return this._http.get<DocumentTypes>(url);
  }

  updateIrisSettings(data: IrisMapping, id: string, entityType: string) {
    const url = `${ADMIN_WORKSPACE_URL}${id}/`;
    return this._http.put(url, {
      entity_type: entityType,
      iris_mapping: data,
    });
  }

  updateSubmissionDocumentTypes(data: SDocumentPayload) {
    const url = `${environment.irisApi}/admin/workspace/type/${data.submissionId}/document_type/`;
    return this._http.put(url, {
      data: data.payload,
      entity_type: data.entityType,
    });
  }
}
